import React, { FC, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { NoData } from '@saucelabs/ds-data-states';
import { LinkButton } from '@saucelabs/ds-link';
import { Helmet } from 'react-helmet';
import { ContentLoader } from '@saucelabs/sl-component-loader';

import useGrowthUserAttributes from '../hooks/useGrowthUserAttribute';
import { graphql, useFragment } from '../graphql/__generated__';
import { VISUAL_ALL_BUILDS_BREADCRUMB, VISUAL_TESTING_BREADCRUMB } from '../global/constants';
import {
  BuildDetailsDiffFragFragment,
  BuildStatus,
  BuildsViewBuildFragmentFragmentDoc,
  ReadBuildDetailsDiffDataDocumentQuery,
} from '../graphql/__generated__/graphql';
import { BuildDetailsFilterBar } from './build-details/BuildDetailsFilterBar';
import { actions, selectors, thunks, types } from '../ducks/buildDetailsFilterBar';
import { actions as buildDetailsActions } from '../ducks/buildDetails';
import { useVisualDispatch, useVisualSelector } from '../reduxHooks';
import BuildsItem from './builds/BuildsItem';
import { DiffViewOverlay } from './diff/DiffViewOverlay';
import { BuildDetailsGroup } from './builds/BuildDetailsGroup';
import DiffHeaderStatus from './diff/DiffHeaderStatus';
import { titleCase } from '../global/utils';
import { ContentErrorHandler } from './common/pages/ContentErrorHandler';
import { BuildDetailsGroupArray } from './build-details/types';
import { dedupe } from './build-details/utils';
import { useVisualWorker } from '../hooks/useVisualWorker';
import { VISUAL_USER_EVENTS } from '../constants';

export const BuildDetailsDiffFrag = graphql(`
  fragment BuildDetailsDiffFrag on Diff {
    id
    status
    snapshot {
      id
      suiteName
      testName
      browserVersion
      browser
      operatingSystemVersion
      operatingSystem
      name
      device
    }
    ...BuildSnapshotThumbFrag @nonreactive
    ...BuildDetailsDiffExpandedFrag @nonreactive
  }
`);

const ReadBuildDetailsDiffDataDocument = graphql(/* GraphQL */ `
  query ReadBuildDetailsDiffDataDocument ($id: UUID!) {
    build(id: $id) {
      id
      project
      diffs {
        nodes {
          id
          ...BuildDetailsDiffFrag @nonreactive
        }
      }
    }
  }
`);

export const BuildDetailsFrag = graphql(`
  fragment BuildDetailsFrag on Build {
    id
    name
    status
    error
    ...BuildsViewBuildFragment
  }
`);

const ReadBuildDetailsDocument = graphql(/* GraphQL */ `
  query ReadBuildDetailsDocument ($id: UUID!) {
    build(id: $id) {
      ...BuildDetailsFrag
    }
  }
`);

export const BuildNameFrag = graphql(`
  fragment BuildNameFrag on Build {
    id
    name
  }
`);

const BUILD_STATUS_POLL_INTERVAL = 30 * 1000; // 30 seconds

export const VisualBuildDetailsPageNew: FC = () => {
  const { buildId, diffId } = useParams<{buildId: string, diffId?: string}>();
  const initState = useVisualSelector(selectors.buildDetailsFilterInitState);
  const filterValues = useVisualSelector(selectors.buildDetailsFilterValues);
  const dispatch = useVisualDispatch();

  const setItemsHaveChanged = useCallback(() => {
    dispatch(buildDetailsActions.setItemsChanged(true));
  }, [dispatch]);

  // Fetch diffs & diff details. Nonreactive & separate, so we can cache items on page without
  // rerendering every time a part of the build updates
  const buildResult = useQuery(ReadBuildDetailsDiffDataDocument, {
    variables: { id: buildId },
  });
  const {
    data,
    refetch,
    loading,
    startPolling: startDiffPolling,
    stopPolling: stopDiffPolling,
    error,
  } = buildResult;
  // Load & fetch build header information & metadata
  const {
    data: buildData,
    loading: buildLoading,
    error: buildError,
    startPolling: startBuildPolling,
    stopPolling: stopBuildPolling,
  } = useQuery(ReadBuildDetailsDocument, {
    variables: { id: buildId },
  });
  const build = useFragment(BuildDetailsFrag, buildData?.build);
  const buildView = useFragment(BuildsViewBuildFragmentFragmentDoc, build);

  const startPolling = useCallback(() => {
    startDiffPolling(BUILD_STATUS_POLL_INTERVAL);
    startBuildPolling(BUILD_STATUS_POLL_INTERVAL);
  }, [startBuildPolling, startDiffPolling]);
  const stopPolling = useCallback(() => {
    stopDiffPolling();
    stopBuildPolling();
  }, [stopBuildPolling, stopDiffPolling]);
  // Automatically poll if buildstatus=running. We'll refresh the results automatically and
  // stop polling if the status changes.
  const isRunning = build?.status === BuildStatus.Running;
  useEffect(() => {
    if (isRunning) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [isRunning, startPolling, stopPolling]);

  // Populate and update filters
  const populateFilters = useCallback((
    result: ReadBuildDetailsDiffDataDocumentQuery | undefined,
  ) => {
    const project = result?.build?.project;
    const diffs = (result?.build?.diffs.nodes ?? []) as BuildDetailsDiffFragFragment[];
    const newState: types.SetFilterBarStatePayload = {
      os: [],
      browser: [],
      device: [],
    };

    diffs.forEach((diff) => {
      // Populate filters
      if (diff.snapshot?.operatingSystem) {
        newState.os.push({
          label: `${titleCase(diff.snapshot.operatingSystem)} ${diff.snapshot.operatingSystemVersion ?? ''}`.trim(),
          key: `${diff.snapshot.operatingSystem}|${diff.snapshot.operatingSystemVersion ?? ''}`.trim(),
        });
      }

      if (diff.snapshot?.device) {
        newState.device.push({
          label: diff.snapshot.device,
          key: diff.snapshot.device,
        });
      }

      if (diff.snapshot?.browser) {
        newState.browser.push({
          label: `${titleCase(diff.snapshot.browser)}`,
          key: diff.snapshot.browser,
        });
      }
    });

    // dedupe filters
    Object.keys(newState).forEach((key) => {
      newState[key] = newState[key].filter(dedupe);
    });

    void dispatch(thunks.initFilters({
      filterValues: newState,
      project,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      populateFilters(data);
    }
  }, [dispatch, data, populateFilters]);

  const [groupState, setGroupState] = useState<{
    groups: BuildDetailsGroupArray,
    diffIds: string[] | null,
  }>({
    groups: {},
    diffIds: null,
  });
  const { groups, diffIds } = groupState;

  const { buildGroups } = useVisualWorker();

  useEffect(() => {
    if (initState === 'succeeded' && data?.build?.diffs.nodes) {
      // Runs our cpu-heavy object / grouping aggregation on a background thread to not block UI
      // updates
      void buildGroups({
        nodes: (data?.build?.diffs.nodes ?? []) as BuildDetailsDiffFragFragment[],
        filterValues,
      })
        .then((groupData) => setGroupState(groupData));
    }
  }, [buildGroups, data?.build?.diffs.nodes, filterValues, initState]);

  useEffect(() => {
    dispatch(buildDetailsActions.setDiffIds(diffIds ?? []));
  }, [diffIds, dispatch]);

  const clearFilters = useCallback(() => {
    dispatch(actions.clearFilters());
  }, [dispatch]);

  useEffect(() => () => {
    dispatch(actions.resetFilters());
    dispatch(buildDetailsActions.resetState());
  }, [dispatch]);

  useGrowthUserAttributes(VISUAL_USER_EVENTS.hasVisitedVisualBuild);

  const isLoading = (loading && !data) ||
    // Filters haven't been initialized yet
    initState === 'idle' ||
    // Data has been loaded, but we haven't generated the aggregation object / diff ids
    ((data?.build?.diffs?.nodes?.length ?? 0) > 0 && !diffIds);

  return (
    <>
      <Helmet>
        <title>
          {build?.name ?? ''}
          {' '}
          | Visual
        </title>
      </Helmet>
      <Breadcrumbs
        list={[
          VISUAL_TESTING_BREADCRUMB,
          VISUAL_ALL_BUILDS_BREADCRUMB,
          { name: build?.name ?? '' },
        ]}
      />
      {buildView && build && (
        <>
          <BuildsItem
            build={buildView}
            isClickable={false}
            onBulkApprove={setItemsHaveChanged}
          />
          <DiffHeaderStatus
            error={build.error}
            status={build.status}
          />
        </>
      )}
      <ContentErrorHandler
        isNotFoundError={!buildData?.build && !buildLoading}
        isServerError={!data && !!(error || buildError)}
        notFoundPageProps={{
          description: "We couldn't find the requested build",
          message: 'The build does not exist or you do not have permissions to view it.',
        }}
      >
        <BuildDetailsFilterBar
          onReload={refetch}
        />
        <ContentLoader show={isLoading}>
          {(diffIds ?? []).length ? (
            <div className="pb-3">
              <BuildDetailsGroup group={groups} />
            </div>
          ) : (
            <NoData
              className="mx-auto"
              description={(
                <>
                  Try updating your search criteria or
                  {' '}
                  <LinkButton onPress={clearFilters}>remove all filters.</LinkButton>
                </>
              )}
              spacing="large"
            />
          )}
        </ContentLoader>
        {diffId && (
          <DiffViewOverlay
            buildId={buildId}
            diffId={diffId}
          />
        )}
      </ContentErrorHandler>
    </>
  );
};
